// ── Email typo detection (mailcheck-style) ──────────────────────────────────
// An invite sent to a misspelled provider domain silently strands the recipient (a real
// production case: an invite went to gmali.com and never arrived, orphaning the talent's
// portal account). Any form that SENDS to a typed email should warn on a likely typo and
// offer the correction — suggestion only, never auto-corrected (some odd domains are real).
/**
 * Domains we consider "known good". Exact matches are never flagged — this list must include
 * legitimate domains that sit within edit distance of each other (mail.com vs gmail.com,
 * live.com vs me.com) or they'd be flagged as typos of one another.
 */
const KNOWN_EMAIL_DOMAINS = [
    'gmail.com',
    'icloud.com',
    'yahoo.com',
    'hotmail.com',
    'outlook.com',
    'aol.com',
    'me.com',
    'mac.com',
    'live.com',
    'msn.com',
    'mail.com',
    'proton.me',
    'protonmail.com',
    'comcast.net',
    'sbcglobal.net',
    'att.net',
    'verizon.net',
    'ymail.com',
    'googlemail.com',
    'hotmail.co.uk',
    'yahoo.co.uk',
    'outlook.co.uk',
];
/** The subset worth suggesting as corrections (the big consumer providers). */
const SUGGESTABLE_DOMAINS = [
    'gmail.com',
    'icloud.com',
    'yahoo.com',
    'hotmail.com',
    'outlook.com',
    'aol.com',
    'comcast.net',
    'protonmail.com',
];
/**
 * Optimal-string-alignment distance (Levenshtein + adjacent transpositions), so the classic
 * swapped-letters typo (gmali → gmail) counts as ONE edit. Small inputs only — O(a·b).
 */
function editDistance(a, b) {
    const rows = a.length + 1;
    const cols = b.length + 1;
    const d = new Array(rows * cols).fill(0);
    const at = (i, j) => i * cols + j;
    for (let i = 0; i < rows; i++)
        d[at(i, 0)] = i;
    for (let j = 0; j < cols; j++)
        d[at(0, j)] = j;
    for (let i = 1; i < rows; i++) {
        for (let j = 1; j < cols; j++) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1;
            let v = Math.min(d[at(i - 1, j)] + 1, d[at(i, j - 1)] + 1, d[at(i - 1, j - 1)] + cost);
            if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
                v = Math.min(v, d[at(i - 2, j - 2)] + 1); // transposition
            }
            d[at(i, j)] = v;
        }
    }
    return d[at(a.length, b.length)];
}
/**
 * If the email's domain looks like a TYPO of a major provider, return the corrected full
 * address (else null). Conservative on purpose: exact known domains never flag; one edit
 * always suggests; two edits only for longer domains (short domains at distance 2 are too
 * often legitimately different). Callers show "Did you mean …?" — never auto-replace.
 */
export function suggestEmailFix(email) {
    const trimmed = email.trim().toLowerCase();
    const atIdx = trimmed.lastIndexOf('@');
    if (atIdx < 1 || atIdx === trimmed.length - 1)
        return null;
    const local = trimmed.slice(0, atIdx);
    const domain = trimmed.slice(atIdx + 1);
    if (!/^[a-z0-9.-]+\.[a-z]{2,}$/.test(domain))
        return null;
    if (KNOWN_EMAIL_DOMAINS.includes(domain))
        return null;
    let best = null;
    for (const known of SUGGESTABLE_DOMAINS) {
        const dist = editDistance(domain, known);
        if (dist === 0)
            return null;
        if (!best || dist < best.dist)
            best = { domain: known, dist };
    }
    if (!best)
        return null;
    const limit = domain.length >= 6 ? 2 : 1;
    return best.dist <= limit ? `${local}@${best.domain}` : null;
}
//# sourceMappingURL=email.js.map